import ko from 'knockout'
import { FeatureFlags } from '@mobi/settings'
import { RunnerNumber } from '@core/Areas/RaceCard/Components/RunnerNumber'
import { MatchedSilkImage } from '@core/Areas/Racing/Components/SilkImage/MatchedSilkImage'
import { subscribeToFeatureChanges } from '@core/State/FeatureFlags/helpers/subscribeToFeatureChanges'
import { Disposable } from '../../../../AppUtils/Framework/Disposable/Disposable'
import Guard from '../../../../AppUtils/Framework/Guard'
import type { IObservableStarter } from '../../../Model/Observables/IObservableStarter'
import { SilkImage } from '../../../Model/SilkImages'

export interface IStarterImageViewModelParams {
  starter: IObservableStarter
  stripes: ko.ObservableArray<number>
  jumperStripes: (arg: number) => string
  numberOfStartersInRace: ko.Computed<number>
}

export class StarterImageViewModel extends Disposable {
  public RunnerNumber: typeof RunnerNumber
  public MatchedSilkImage: typeof MatchedSilkImage
  public isFieldSummaryV2Active: ko.Observable<boolean> = ko.observable(false)

  private ldSubscription: () => void

  constructor(params: IStarterImageViewModelParams) {
    super()

    this.RunnerNumber = RunnerNumber
    this.MatchedSilkImage = MatchedSilkImage

    Guard.notNull(params)
    Guard.notNull(params.starter)

    this.ldSubscription = subscribeToFeatureChanges(
      FeatureFlags.FIELD_SUMMARY_V2.key,
      fieldSummaryV2 => {
        if (fieldSummaryV2 !== this.isFieldSummaryV2Active()) {
          this.isFieldSummaryV2Active(fieldSummaryV2)
        }
      }
    )

    this.starter = params.starter
    this.stripes = params.stripes
    this.jumperStripes = params.jumperStripes

    this.key = this.starter.type()[0] + this.starter.name().toUpperCase()

    this.cssClass = this.starter.tag() === 'Dog' ? '' : `silk ${this.starter.tag().toLowerCase()}`

    this.isTrot = ko.pureComputed<boolean>(() => {
      return this.starter.type() === 'Trots'
    }, this)

    this.isDog = ko.pureComputed<boolean>(() => {
      return this.starter.type() === 'Dogs'
    }, this)

    this.hasSilk = ko.pureComputed<boolean>(() => {
      return typeof this.starter.silkImages() !== 'undefined'
    }, this)

    this.silkImage = ko.pureComputed<SilkImage | null>(() => {
      return this.hasSilk() ? this.starter.silkImages().getSilkImageFor(window.innerWidth) : null
    }, this)

    this.widthCss = ko.pureComputed<string>(() => {
      return this.hasSilk() && !this.isDog()
        ? `${(this.silkImage() as SilkImage).size.width}px`
        : ''
    }, this)

    this.heightCss = ko.pureComputed<string>(() => {
      return this.hasSilk() && !this.isDog()
        ? `${(this.silkImage() as SilkImage).size.height}px`
        : ''
    }, this)

    this.backgroundCss = ko.pureComputed<string>(() => {
      return this.hasSilk()
        ? (this.silkImage() as SilkImage).getCssBackgroundForStarter(
            this.starter.number(),
            this.starter.type(),
            params.numberOfStartersInRace ? params.numberOfStartersInRace() : 0
          )
        : ''
    }, this)

    this.configureDisposal()
  }
  private configureDisposal() {
    this.registerDisposals(() => {
      this.ldSubscription()
    })
  }

  public starter: IObservableStarter
  public key: string
  public cssClass: string
  public stripes: ko.ObservableArray<number>
  public jumperStripes: (arg: number) => string
  public isTrot: ko.PureComputed<boolean>
  public isDog: ko.PureComputed<boolean>
  public hasSilk: ko.PureComputed<boolean>
  public silkImage: ko.PureComputed<SilkImage | null>
  public widthCss: ko.PureComputed<string>
  public heightCss: ko.PureComputed<string>
  public backgroundCss: ko.PureComputed<string>
}
